import { useMutation, useQuery, useQueryClient } from "react-query";
import type { UseQueryOptions } from "react-query";
import config from "../../config";
import type { UboDetailsResponse } from "../../modules/aml/types/aml.type";
import type { ReportsListItem } from "../../modules/generate-reports/report.types";
import type { DeleteProfileDetailsNotes } from "../../modules/profiles/components/ViewNotes";
import type { UpdateProfileNotesPayload } from "../../modules/profiles/custom-components/EditableTextInput";
import { transformProfilesArray } from "../../modules/profiles/profile-mapper";
import type {
  Addresses,
  AddressesResponse,
  SaveAddressToProfileMutation,
} from "../../modules/profiles/types/address.type";
import type { UpdateAmlSearchUpdatePayload } from "../../modules/profiles/profile-view/profile-details/aml-screen/aml-screen-list/AmlScreenInterfaces";
import type {
  AmlScreenMonitoringContainer,
  AmlScreenMonitoringResponse,
  AmlScreenMonitoringProps,
  AmlScreenMonitoringUpdatePayload,
  AmlSearchesProps,
  DeleteAmlSearchesPayload,
  GetMonitoringSchedule,
  GetMonitoringScheduleById,
} from "../../modules/profiles/types/amlScreen.type";
import type {
  CreateProfilePayload,
  CreateProfileResponse,
} from "../../modules/profiles/types/createProfile.type";
import type { CurrencyCodeResponse } from "../../modules/profiles/types/currency.type";
import type {
  AddNewKeyPartyMutation,
  Attachments,
  AttachmentsUpdate,
  CreateProfileExport,
  DownloadAttachment,
  ExportProfileResponse,
  GetAttachmentResponse,
  GetKeyPartiesQueryParams,
  GetNotesResponse,
  GetProfilesResponse,
  KeyPartiesResponse,
  KeyPartiesType,
  NotesResponse,
  Profile,
  ProfileTypes,
  SaveNotesPayload,
  UpdateKeyPartyMutation,
  UpdateProfilePayload,
  UploadDocumentPayload,
} from "../../modules/profiles/types/profile.type";
import type {
  ProfileDetails,
  UpdateProfileDetailsPayload,
} from "../../modules/profiles/types/profileDetails.type";
import {
  apiVersion,
  connectApiVersion,
} from "../../shared/constants/common.constant";
import type { Attachment } from "../../shared/types/attachment.type";
import type { CountryCodeResponse } from "../../shared/types/country.type";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import type {
  Pagination,
  PaginationQuery,
  Paginator,
} from "../../shared/types/table.type";
import { createQueryString } from "../../utils";
import {
  deleteAsync,
  getAsync,
  patchAsync,
  postAsync,
  putAsync,
} from "../protect-api";
import { PROFILE_REPORTS } from "../reports";
import type { ActiveUser } from "../../store/UserSlice";

export const PROFILES_KEY = "profiles";
export const PROFILE_TYPES_KEY = "profile-types";
export const PROFILE_HEADER_KEY = "profile-header";
export const PROFILES_DETAILS_KEY = "profile-details";
export const PROFILES_DETAILS_ADDRESSES_KEY = "profile-details-addresses";
export const CURRENCY_CODE_KEY = "currency-codes";
export const COUNTRY_CODE_KEY = "country-codes";
export const NOTES_KEY = "notes";
export const PROFILE_NOTES_KEY = "profile-notes";
export const PROFILE_NOTES_DELETE_KEY = "profile-notes-delete";
export const AML_SCREEN_KEY = "aml-screen";
export const AUDITS_KEY = "audits";
export const UBO_DETAILS_KEY = "ubo-details";
export const KEYPARTIES_BY_PROFILE_KEY = "keyparties-by-profile";
export const KEYPARTIES_BY_PROFILE_AML_SCREEN_KEY =
  "keyparties-by-profile-aml-screen";
export const UBOS_BY_PROFILE_KEY = "ubos-by-profile";
export const UBOS_BY_PROFILE_AML_SCREEN_KEY = "ubos-by-profile-aml-screen";
export const KEYPARTY_UBO_AML_SCREEN = "keyparties-ubo-aml-by-profile";
export const ADD_SEARCH_TYPE_TO_PROFILE_KEY =
  "add-aml-to-search-get-keyparites";
export const MONITORING_COUNTRY_CODE_KEY = "monitoring-country-codes";

export const getProfiles = async (
  query: PaginationQuery
): Promise<GetProfilesResponse> => {
  try {
    const { data } = await getAsync(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles?${createQueryString(
        query
      )}`
    );
    return {
      total: Number.parseInt(data?.count ?? data?.totalSize, 10),
      profiles: transformProfilesArray(data.items),
      apiFailed: false,
    };
  } catch {
    return {
      total: 0,
      profiles: [],
      apiFailed: true,
    };
  }
};

export const useGetProfiles = (
  query: PaginationQuery
): ReturnType<typeof useQuery<GetProfilesResponse, ProblemDetails>> => {
  return useQuery<GetProfilesResponse, ProblemDetails>(
    [PROFILES_KEY, query],
    () => getProfiles(query)
  );
};

const getAttachmentUrl = async ({
  profileId,
  attachmentId,
}: {
  profileId: string;
  attachmentId: string;
}): Promise<DownloadAttachment> => {
  try {
    const { data } = await getAsync(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/attachments/${attachmentId}/download`
    );
    return {
      downloadUrl: data.downloadUrl,
      fileName: data.fileName,
    };
  } catch {
    return {
      downloadUrl: "",
      fileName: "",
    };
  }
};

export const useGetAttachmentUrl = (): ReturnType<
  typeof useMutation<
    DownloadAttachment,
    ProblemDetails,
    { profileId: string; attachmentId: string }
  >
> => useMutation(getAttachmentUrl);

export const getAttachments = async (
  profileId: string,
  query: Pagination
): Promise<GetAttachmentResponse> => {
  try {
    const { data } = await getAsync(
      `${
        config.COMPLIANCE_API_URL
      }/${apiVersion}/profiles/${profileId}/attachments?${createQueryString(
        query
      )}`
    );
    return {
      attachments: data.items,
      count: data?.count ?? data?.totalSize ?? 0,
      apiFailed: false,
    };
  } catch {
    return {
      attachments: [],
      count: 0,
      apiFailed: true,
    };
  }
};

export const useGetAttachments = (
  profileId: string,
  query: Pagination,
  enabled = false
): ReturnType<typeof useQuery<GetAttachmentResponse, ProblemDetails>> => {
  return useQuery<GetAttachmentResponse, ProblemDetails>(
    [
      PROFILES_KEY,
      profileId,
      "attachments",
      query.page,
      query.pageSize,
      query.searchTerm,
    ],
    () => getAttachments(profileId, query),
    {
      enabled,
      staleTime: 0,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
    }
  );
};

export const saveAddressForProfile = async (
  profileId: string,
  address: Addresses
): Promise<Addresses> => {
  const { data } = await postAsync<Addresses>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/details/addresses`,
    address
  );

  return data;
};

const saveAddressToProfile = async ({
  profileId,
  payload,
}: SaveAddressToProfileMutation): Promise<AddressesResponse> => {
  const { data } = await postAsync<AddressesResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/details/addresses`,
    payload
  );
  return data;
};

export const useSaveAddressForProfile = (): ReturnType<
  typeof useMutation<
    AddressesResponse,
    ProblemDetails,
    SaveAddressToProfileMutation
  >
> => {
  return useMutation<
    AddressesResponse,
    ProblemDetails,
    SaveAddressToProfileMutation
  >(saveAddressToProfile);
};

export const editAddressForProfile = async (
  profileId: string,
  id: string,
  address: Addresses
): Promise<Addresses> => {
  const { data } = await putAsync<Addresses>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/details/addresses/${id}`,
    address
  );

  return data;
};

export const getProfileById = async (id: string): Promise<Profile> => {
  const { data } = await getAsync<Profile>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${id}`
  );

  return data;
};

export const useGetProfileById = (
  id: string,
  options?: UseQueryOptions<Profile, ProblemDetails>
): ReturnType<typeof useQuery<Profile, ProblemDetails>> => {
  return useQuery<Profile, ProblemDetails>(
    [PROFILE_HEADER_KEY, id],
    () => getProfileById(id),
    {
      keepPreviousData: true,
      ...options,
    }
  );
};

const getUsersForProfiles = async (): Promise<ActiveUser[]> => {
  const { data } = await getAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/users/active`,
    undefined,
    undefined,
    true
  );
  return data;
};

export const useGetUsers = (): ReturnType<
  typeof useQuery<ActiveUser[], ProblemDetails>
> => {
  return useQuery<ActiveUser[], ProblemDetails>(["USERS_FOR_PROFILE"], () =>
    getUsersForProfiles()
  );
};

const deleteProfiles = async (profileIds: string[]): Promise<void> => {
  await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles`,
    profileIds
  );
};

export const useDeleteProfiles = (): ReturnType<
  typeof useMutation<void, ProblemDetails, string[]>
> => useMutation(deleteProfiles);

const assignProfilesToUser = async ({
  profileIds,
  userId,
}: {
  profileIds: string[];
  userId: number;
}): Promise<void> => {
  await putAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/assign/bulk`,
    {
      profileIds,
      userId: userId > 0 ? userId : null,
    }
  );
};

export const useAssignProfilesToUser = (): ReturnType<
  typeof useMutation<
    void,
    ProblemDetails,
    { profileIds: string[]; userId: number }
  >
> => useMutation(assignProfilesToUser);

const getProfileTypes = async (): Promise<ProfileTypes> => {
  const { data } = await getAsync<ProfileTypes>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/types`
  );
  return data;
};

export const useGetProfileTypes = (): ReturnType<
  typeof useQuery<ProfileTypes, ProblemDetails>
> => {
  return useQuery<ProfileTypes, ProblemDetails>(
    [PROFILE_TYPES_KEY],
    () => getProfileTypes(),
    { keepPreviousData: true }
  );
};

type UpdateProfilePatchPayload = Pick<UpdateProfilePayload, "id"> &
  Partial<Omit<UpdateProfilePayload, "id">>;

const updateProfile = async (
  payload: UpdateProfilePatchPayload
): Promise<Profile> => {
  const { id, ...patchPayload } = payload;

  const response = await patchAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${id}`,
    patchPayload,
    {},
    true
  );
  return response.data;
};

export const useUpdateProfile = (): ReturnType<
  typeof useMutation<Profile, ProblemDetails, UpdateProfilePatchPayload>
> =>
  useMutation<Profile, ProblemDetails, UpdateProfilePatchPayload>(
    updateProfile
  );

const getCurrencyCode = async (): Promise<CurrencyCodeResponse> => {
  try {
    const { data } = await getAsync(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/lookup/currencyCodes`
    );
    return {
      currencyCode: data,
    };
  } catch {
    return {
      currencyCode: [],
    };
  }
};

export const useGetCurrencyCode = (): ReturnType<
  typeof useQuery<CurrencyCodeResponse, ProblemDetails>
> => {
  return useQuery<CurrencyCodeResponse, ProblemDetails>(
    [CURRENCY_CODE_KEY],
    () => getCurrencyCode()
  );
};

export const getCountries = async (): Promise<CountryCodeResponse> => {
  try {
    const { data } = await getAsync(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/lookup/countryCodes`
    );
    return {
      countryCodes: data,
    };
  } catch {
    return {
      countryCodes: [],
    };
  }
};

export const useGetCountryCode = (): ReturnType<
  typeof useQuery<CountryCodeResponse, ProblemDetails>
> => {
  return useQuery<CountryCodeResponse, ProblemDetails>(
    [COUNTRY_CODE_KEY],
    () => getCountries(),
    { staleTime: Infinity }
  );
};

const getProfileDetailsById = async (id: string): Promise<ProfileDetails> => {
  const { data } = await getAsync<ProfileDetails>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${id}/details`
  );

  return data;
};

export const useGetProfileDetailsById = (
  id: string
): ReturnType<typeof useQuery<ProfileDetails, ProblemDetails>> => {
  return useQuery<ProfileDetails, ProblemDetails>(
    [PROFILES_DETAILS_KEY, id],
    () => getProfileDetailsById(id)
  );
};

export const updateProfileDetailsById = async (
  id: string,
  payload: UpdateProfileDetailsPayload
): Promise<ProfileDetails> => {
  const response = await putAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${id}/details`,
    payload
  );
  return response.data;
};

const getProfileDetailsAddressesById = async (
  id: string
): Promise<Addresses[]> => {
  const { data } = await getAsync<Addresses[]>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${id}/details/addresses`
  );

  return data;
};

export const useGetProfileDetailsAddressesById = (
  id: string
): ReturnType<typeof useQuery<Addresses[], ProblemDetails>> => {
  return useQuery<Addresses[], ProblemDetails>(
    [PROFILES_DETAILS_ADDRESSES_KEY, id],
    () => getProfileDetailsAddressesById(id)
  );
};

const createProfile = async <TPayload extends CreateProfilePayload>(
  payload: TPayload
): Promise<CreateProfileResponse> => {
  const { data } = await postAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles`,
    payload
  );
  return data;
};

export const useCreateProfile = <
  TPayload extends CreateProfilePayload
>(): ReturnType<
  typeof useMutation<CreateProfileResponse, ProblemDetails, TPayload>
> => {
  const queryClient = useQueryClient();
  return useMutation<CreateProfileResponse, ProblemDetails, TPayload>(
    createProfile<TPayload>,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROFILES_KEY);
      },
    }
  );
};

const uploadDocumentsToProfile = async (
  profileId: string,
  payload: Attachment
): Promise<Attachments> => {
  const uploadFormData = new FormData();

  uploadFormData.append("file", payload.file);
  uploadFormData.append("DocumentType", payload.documentType);

  if (payload.description !== undefined) {
    uploadFormData.append("Description", payload.description);
  }

  const { data } = await postAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/attachments`,
    uploadFormData
  );
  return data;
};

export const useUploadDocumentsToProfile = (): ReturnType<
  typeof useMutation<
    Attachments,
    ProblemDetails,
    { profileId: string; payload: Attachment }
  >
> => {
  const queryClient = useQueryClient();
  return useMutation<
    Attachments,
    ProblemDetails,
    { profileId: string; payload: Attachment }
  >(
    async ({ profileId, payload }) => {
      return await uploadDocumentsToProfile(profileId, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROFILE_HEADER_KEY);
        queryClient.invalidateQueries(PROFILES_KEY);
      },
    }
  );
};

const deleteAttachmentsFromProfile = async (
  profileId: string,
  attachmentId: string
): Promise<unknown> => {
  const { data } = await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/attachments/${attachmentId}`
  );
  return data;
};

export const useDeleteAttachmentsFromProfile = (): ReturnType<
  typeof useMutation<
    unknown,
    ProblemDetails,
    { profileId: string; attachmentId: string }
  >
> => {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    ProblemDetails,
    { profileId: string; attachmentId: string }
  >(
    async ({ profileId, attachmentId }) => {
      return await deleteAttachmentsFromProfile(profileId, attachmentId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROFILES_KEY);
        queryClient.invalidateQueries(PROFILE_HEADER_KEY);
      },
    }
  );
};

export const editAttachmentsForProfile = async (
  profileId: string,
  attachmentId: string,
  dataToUpdate: AttachmentsUpdate
): Promise<Attachments> => {
  const { data } = await putAsync<Attachments>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/attachments/${attachmentId}`,
    dataToUpdate
  );

  return data;
};

const uploadDocuments = async (
  payload: UploadDocumentPayload
): Promise<Attachment> => {
  const uploadFormData = new FormData();

  uploadFormData.append("file", payload.file);
  uploadFormData.append("DocumentType", payload.documentType);

  if (payload.description !== undefined) {
    uploadFormData.append("Description", payload.description);
  }

  const { data } = await postAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${payload.profileId}/attachments`,
    uploadFormData
  );
  return data;
};

export const useUploadDocuments = (): ReturnType<
  typeof useMutation<Attachment, ProblemDetails, UploadDocumentPayload>
> => {
  const queryClient = useQueryClient();
  return useMutation<Attachment, ProblemDetails, UploadDocumentPayload>(
    uploadDocuments,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROFILES_KEY);
        queryClient.invalidateQueries(PROFILE_HEADER_KEY);
      },
    }
  );
};

const saveNotesToProfile = async ({
  profileId,
  payload,
}: SaveNotesPayload): Promise<NotesResponse> => {
  const { data } = await postAsync<NotesResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/notes`,
    payload
  );
  return data;
};

export const useSaveNotesForProfile = (): ReturnType<
  typeof useMutation<NotesResponse, ProblemDetails, SaveNotesPayload>
> => {
  const queryClient = useQueryClient();

  return useMutation<NotesResponse, ProblemDetails, SaveNotesPayload>(
    saveNotesToProfile,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROFILE_HEADER_KEY);
        queryClient.invalidateQueries(NOTES_KEY);
        queryClient.invalidateQueries(PROFILES_DETAILS_KEY);
      },
    }
  );
};

export const getNotes = async (
  profileId: string,
  query: Pagination
): Promise<GetNotesResponse> => {
  try {
    const { data } = await getAsync(
      `${
        config.COMPLIANCE_API_URL
      }/${apiVersion}/profiles/${profileId}/notes?${createQueryString(query)}`
    );
    return {
      total: Number.parseInt(data?.count ?? data?.totalSize, 10),
      notes: data.items,
      apiFailed: false,
    };
  } catch {
    return {
      total: 0,
      notes: [],
      apiFailed: true,
    };
  }
};

export const useGetNotes = (
  profileId: string,
  query: Pagination
): ReturnType<typeof useQuery<GetNotesResponse, ProblemDetails>> => {
  return useQuery<GetNotesResponse, ProblemDetails>([NOTES_KEY, query], () =>
    getNotes(profileId, query)
  );
};

export const updateProfileNotesById = async ({
  profileId,
  noteId,
  payload,
}: UpdateProfileNotesPayload): Promise<NotesResponse> => {
  const { data } = await putAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/notes/${noteId}`,
    payload
  );
  return data;
};

export const useUpdateProfileNotesById = (): ReturnType<
  typeof useMutation<NotesResponse, ProblemDetails, UpdateProfileNotesPayload>
> => useMutation(updateProfileNotesById);

export const deleteProfileNotes = async ({
  profileId,
  noteId,
}: DeleteProfileDetailsNotes): Promise<unknown> => {
  const response = await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/notes/${noteId}`
  );
  return response.data;
};

export const useDeleteProfileNotes = (): ReturnType<
  typeof useMutation<unknown, ProblemDetails, DeleteProfileDetailsNotes>
> => {
  const queryClient = useQueryClient();

  return useMutation(deleteProfileNotes, {
    onSuccess: () => {
      queryClient.invalidateQueries(PROFILE_HEADER_KEY);
      queryClient.invalidateQueries(PROFILE_NOTES_KEY);
      queryClient.invalidateQueries(NOTES_KEY);
      queryClient.invalidateQueries(PROFILES_DETAILS_KEY);
    },
  });
};

const addToMonitoring = async (
  monitoringDetails: AmlScreenMonitoringContainer
): Promise<AmlScreenMonitoringResponse> => {
  const { data } = await postAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/schedules`,
    monitoringDetails
  );
  return data;
};

const getAmlSearches = async ({
  type,
  searchId,
}: AmlSearchesProps): Promise<unknown> => {
  const { data } = await getAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${type}/${searchId}`
  );
  return data;
};

const getMonitoringSchedule = async ({
  scheduleId,
  profileId,
}: GetMonitoringSchedule): Promise<AmlScreenMonitoringContainer> => {
  const { data } = await getAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/schedules/${scheduleId}`
  );
  return data;
};

const getMonitoringScheduleById = async ({
  scheduleId,
}: GetMonitoringScheduleById): Promise<AmlScreenMonitoringContainer> => {
  const { data } = await getAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/schedules/${scheduleId}`
  );
  return data;
};

const updateMonitoringBulk = async (
  monitoringDetails: AmlScreenMonitoringUpdatePayload
): Promise<unknown> => {
  const data = await putAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/schedules`,
    monitoringDetails
  );
  return data;
};

const updateMonitoring = async ({
  scheduleId,
  monitoringDetails,
}: AmlScreenMonitoringProps): Promise<unknown> => {
  const { data } = await putAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/schedules/${scheduleId}`,
    monitoringDetails
  );
  return data;
};

const deleteFromMonitoring = async (scheduleId: string): Promise<unknown> => {
  const { data } = await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/schedules/${scheduleId}`
  );
  return data;
};

export const useAddToMonitoring = (): ReturnType<
  typeof useMutation<
    AmlScreenMonitoringResponse,
    ProblemDetails,
    AmlScreenMonitoringContainer
  >
> => useMutation(addToMonitoring);
export const useUpdateMonitoring = (): ReturnType<
  typeof useMutation<unknown, ProblemDetails, AmlScreenMonitoringProps>
> => useMutation(updateMonitoring);
export const useUpdateMonitoringBulk = (): ReturnType<
  typeof useMutation<unknown, ProblemDetails, AmlScreenMonitoringUpdatePayload>
> => useMutation(updateMonitoringBulk);

export const useDeleteFromMonitoring = (): ReturnType<
  typeof useMutation<unknown, ProblemDetails, string>
> => useMutation(deleteFromMonitoring);
export const useGetMonitoringSchedules = (): ReturnType<
  typeof useMutation<
    AmlScreenMonitoringContainer,
    ProblemDetails,
    GetMonitoringSchedule
  >
> => useMutation(getMonitoringSchedule);
export const useGetMonitoringScheduleById = (): ReturnType<
  typeof useMutation<
    AmlScreenMonitoringContainer,
    ProblemDetails,
    GetMonitoringScheduleById
  >
> => useMutation(getMonitoringScheduleById);
export const useGetAmlSearches = (): ReturnType<
  typeof useMutation<unknown, ProblemDetails, AmlSearchesProps>
> => useMutation(getAmlSearches);

const deleteProfileAddress = async ({
  profileId,
  addressId,
}: {
  profileId: string;
  addressId: string;
}): Promise<void> => {
  await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/details/addresses/${addressId}`
  );
};

export const useDeleteProfileAddress = (): ReturnType<
  typeof useMutation<
    void,
    ProblemDetails,
    { profileId: string; addressId: string }
  >
> => useMutation(deleteProfileAddress);

const addNewKeyParty = async ({
  profileId,
  keyPartyPayload,
}: AddNewKeyPartyMutation): Promise<KeyPartiesType> => {
  const { data } = await postAsync<KeyPartiesType>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties`,
    keyPartyPayload
  );
  return data;
};

const updateKeyParty = async ({
  profileId,
  keyPartyPayload,
  keyPartyId,
}: UpdateKeyPartyMutation): Promise<KeyPartiesType> => {
  const { data } = await putAsync<KeyPartiesType>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/${keyPartyId}`,
    keyPartyPayload
  );
  return data;
};

const createProfileExport = async ({
  exportPayload,
}: CreateProfileExport): Promise<ExportProfileResponse> => {
  const { data } = await postAsync<ExportProfileResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/business/import`,
    exportPayload
  );
  return data;
};

const createProfileExportConnect = async ({
  exportPayload,
  queryParams,
}: CreateProfileExport): Promise<ExportProfileResponse> => {
  const { data } = await postAsync<ExportProfileResponse>(
    `${
      config.UPP_CONNECT_API_URL
    }/${connectApiVersion}/compliance/kyc-protect/profiles/import/companies/${
      exportPayload.companyId
    }?${createQueryString(queryParams)}`,
    exportPayload,
    undefined,
    true
  );
  return data;
};

export const deleteKeyParty = async ({
  profileId,
  keyPartyId,
}: {
  profileId: string;
  keyPartyId: string;
}): Promise<unknown> => {
  const response = await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/${keyPartyId}`
  );
  return response.data;
};

const saveAMLSearchHitNote = async ({
  note,
}: {
  note: string;
}): Promise<string> => {
  const { data } = await postAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/hit/notes/123`,
    {
      note: note,
    }
  );
  return data;
};

export const useAddNewKeyParty = (): ReturnType<
  typeof useMutation<KeyPartiesType, ProblemDetails, AddNewKeyPartyMutation>
> => {
  return useMutation<KeyPartiesType, ProblemDetails, AddNewKeyPartyMutation>(
    addNewKeyParty
  );
};

export const useUpdateKeyParty = (): ReturnType<
  typeof useMutation<KeyPartiesType, ProblemDetails, UpdateKeyPartyMutation>
> => {
  return useMutation<KeyPartiesType, ProblemDetails, UpdateKeyPartyMutation>(
    updateKeyParty
  );
};

export const useCreateProfileExport = (): ReturnType<
  typeof useMutation<ExportProfileResponse, ProblemDetails, CreateProfileExport>
> => {
  return useMutation(createProfileExport);
};

export const useCreateProfileExportConnect = (): ReturnType<
  typeof useMutation<ExportProfileResponse, ProblemDetails, CreateProfileExport>
> => {
  return useMutation(createProfileExportConnect);
};

export const useDeleteKeyParty = (): ReturnType<
  typeof useMutation<
    unknown,
    ProblemDetails,
    { profileId: string; keyPartyId: string }
  >
> => useMutation(deleteKeyParty);
export const useSaveAMLSearchHitNote = (): ReturnType<
  typeof useMutation<string, ProblemDetails, { note: string }>
> => useMutation(saveAMLSearchHitNote);

const getUboDetails = async (
  profileId: string,
  query: Paginator
): Promise<UboDetailsResponse> => {
  const response = await getAsync<UboDetailsResponse>(
    `${
      config.COMPLIANCE_API_URL
    }/${apiVersion}/profiles/${profileId}/ubo?${createQueryString(query)}`
  );
  return response.data;
};

export const useGetUboDetails = (
  searchId: string,
  query: Paginator
): ReturnType<typeof useQuery<UboDetailsResponse>> => {
  return useQuery<UboDetailsResponse>([UBO_DETAILS_KEY, query], () =>
    getUboDetails(searchId, query)
  );
};

const getKeyPartiesByProfile = async (
  profileId: string,
  query: GetKeyPartiesQueryParams
): Promise<KeyPartiesResponse> => {
  try {
    const { data } = await getAsync<KeyPartiesResponse>(
      `${
        config.COMPLIANCE_API_URL
      }/${apiVersion}/profiles/${profileId}/keyparties?${createQueryString(
        query
      )}`
    );
    return {
      count: data?.count ?? data?.totalSize ?? 0,
      items: data.items,
      apiFailed: false,
    };
  } catch {
    return {
      count: 0,
      items: [],
      apiFailed: true,
    };
  }
};

export const useGetKeyPartiesByProfile = (
  profileId: string,
  queryKey: string,
  queryParams: GetKeyPartiesQueryParams
): ReturnType<typeof useQuery<KeyPartiesResponse>> => {
  return useQuery<KeyPartiesResponse>([queryKey, queryParams.page], () =>
    getKeyPartiesByProfile(profileId, queryParams)
  );
};
const updateBusinessAmlSearches = async ({
  searchId,
  payload,
}: {
  searchId: string;
  payload: UpdateAmlSearchUpdatePayload;
}): Promise<UpdateAmlSearchUpdatePayload> => {
  const { data } = await patchAsync<UpdateAmlSearchUpdatePayload>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/businesses/${searchId}`,
    payload,
    {},
    true
  );

  return data;
};

const updateIndividualAmlSearches = async ({
  searchId,
  payload,
}: {
  searchId: string;
  payload: UpdateAmlSearchUpdatePayload;
}): Promise<UpdateAmlSearchUpdatePayload> => {
  const { data } = await patchAsync<UpdateAmlSearchUpdatePayload>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/individuals/${searchId}`,
    payload,
    {},
    true
  );

  return data;
};

export const useUpdateBusinessAmlSearches = (): ReturnType<
  typeof useMutation<
    UpdateAmlSearchUpdatePayload,
    ProblemDetails,
    { searchId: string; payload: UpdateAmlSearchUpdatePayload }
  >
> => {
  return useMutation(updateBusinessAmlSearches);
};

export const useUpdateIndividualAmlSearches = (): ReturnType<
  typeof useMutation<
    UpdateAmlSearchUpdatePayload,
    ProblemDetails,
    { searchId: string; payload: UpdateAmlSearchUpdatePayload }
  >
> => {
  return useMutation(updateIndividualAmlSearches);
};

export const deleteAmlSearch = async ({
  profileId,
  searchIds,
}: DeleteAmlSearchesPayload): Promise<unknown> => {
  const response = await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/searches`,
    searchIds
  );
  return response.data;
};

export const deleteKeyPartiesAmlSearch = async ({
  profileId,
  keyPartiesPayload,
}: DeleteAmlSearchesPayload): Promise<unknown> => {
  const response = await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/searches`,
    keyPartiesPayload
  );
  return response.data;
};

export const useDeleteAmlSearch = (): ReturnType<
  typeof useMutation<unknown, ProblemDetails, DeleteAmlSearchesPayload>
> => useMutation(deleteAmlSearch);

export const useDeleteKeyPartiesAmlSearch = (): ReturnType<
  typeof useMutation<unknown, ProblemDetails, DeleteAmlSearchesPayload>
> => useMutation(deleteKeyPartiesAmlSearch);

const postProfileToKYCMonitoring = async ({
  profileIds,
}: {
  profileIds: string[];
}): Promise<boolean> => {
  const { data } = await postAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles/bulk`,
    {
      items: profileIds,
    }
  );
  if (data?.successful && data?.successful.length > 0) {
    return true;
  }
  return false;
};

export const usePostProfileToKYCMonitoring = (): ReturnType<
  typeof useMutation<boolean, ProblemDetails, { profileIds: string[] }>
> => useMutation(postProfileToKYCMonitoring);

export const removeProfileFromKYCMonitoring = async ({
  profileIds,
}: {
  profileIds: string[];
}): Promise<unknown> => {
  const response = await deleteAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles`,
    {
      items: profileIds,
    }
  );
  return response.data;
};

export const useRemoveProfileFromKYCMonitoring = (): ReturnType<
  typeof useMutation<unknown, ProblemDetails, { profileIds: string[] }>
> => useMutation(removeProfileFromKYCMonitoring);

export const getMonitoringCountries =
  async (): Promise<CountryCodeResponse> => {
    try {
      const { data } = await getAsync(
        `${config.COMPLIANCE_API_URL}/${apiVersion}/lookup/kycMonitoring/countryCodes`
      );
      return {
        countryCodes: data ?? [],
      };
    } catch {
      return {
        countryCodes: [],
      };
    }
  };

export const useGetMonitoringCountries = (): ReturnType<
  typeof useQuery<CountryCodeResponse, ProblemDetails>
> => {
  return useQuery<CountryCodeResponse, ProblemDetails>(
    [MONITORING_COUNTRY_CODE_KEY],
    () => getMonitoringCountries(),
    { staleTime: Infinity }
  );
};

export const getProfileReportById = async (
  profileId: string
): Promise<ReportsListItem[]> => {
  const { data } = await getAsync<ReportsListItem[]>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/Profiles/${profileId}/reports`,
    undefined,
    undefined,
    true
  );
  return data;
};

export const useGetProfileReportById = (
  id: string,
  options?: UseQueryOptions<ReportsListItem[], ProblemDetails>
): ReturnType<typeof useQuery<ReportsListItem[], ProblemDetails>> => {
  return useQuery<ReportsListItem[], ProblemDetails>(
    [PROFILE_REPORTS, id],
    () => getProfileReportById(id),
    {
      keepPreviousData: true,
      ...options,
    }
  );
};
